/**
 * @callback ScrollObserverCallback
 * @param {ScrollObserverEntry} entry
 * @param {ScrollObserver} observer
 * @returns {void}
 */

/**
 * @typedef {Object} ScrollObserverOptions
 * @param {number} frame
 * @param {number} threshold
 */

/**
 * @typedef {ScrollObserverEntry[] & { timeStamp: DOMHighResTimeStamp; }} ScrollObserverEntryStack
 */

export default class ScrollObserver {
  /**
   *
   * @param {ScrollObserverCallback} callback
   * @param {Partial<ScrollObserverOptions>} [options]
   */
  constructor(callback, options = {}) {
    this.callback = callback;
    this.entries = new WeakMap();
    this.options = { frame: 75, threshold: 1, ...options };
  }

  /** @type {ScrollObserverOptions} */
  options;

  /** @type {WeakMap<Element, ScrollObserverEntryStack | null>} */
  entries;

  /** @type {ScrollObserverCallback}  */
  callback;

  /**
   * @param {Event} event
   */
  handleEvent(event) {
    const target = event.currentTarget;
    if (target instanceof Element && this.entries.has(target)) {
      target.removeEventListener(event.type, this);
      this.requestAnimationFrame(target);
    }
  }

  /**
   * @param {Element} target
   */
  observe(target) {
    this.entries.set(target, null);
    target.addEventListener("scroll", this);
  }

  /**
   * @param {Element} target
   */
  requestAnimationFrame(target) {
    const frameRequestCallback = (now = performance.now()) => {
      /** @type {ScrollObserverEntryStack} */
      const stack =
        this.entries.get(target) || Object.assign([], { timeStamp: now });

      while (stack.timeStamp - stack[0]?.timeStamp > this.options.frame) {
        stack.shift();
      }

      const entry = new ScrollObserverEntry(target, this);

      if (entry.movementX || entry.movementY) {
        stack.timeStamp = entry.timeStamp;
      }

      this.callback(entry, this);

      if (entry.isScrollEnd) {
        this.entries.set(target, null);
        target.addEventListener("scroll", this);
      } else {
        stack.push(entry);
        this.entries.set(target, stack);
        requestAnimationFrame(frameRequestCallback);
      }
    };

    frameRequestCallback();
  }

  /**
   * @param {Element} target
   */
  unobserve(target) {
    this.entries.delete(target);
    target.removeEventListener("scroll", this);
  }
}

class ScrollObserverEntry {
  /**
   * @param {Element} target
   * @param {ScrollObserver} observer
   */
  constructor(target, observer) {
    const { frame, threshold } = observer.options;
    const stack = observer.entries.get(target);

    this.target = target;
    this.isScrollStart = true;
    this.isScrollEnd = false;
    this.isScrolling = false;
    this.x = target.scrollLeft;
    this.y = target.scrollTop;
    this.movementX = 0;
    this.movementY = 0;
    this.rateX = 0;
    this.rateY = 0;
    this.signX = 0;
    this.signY = 0;
    this.timeStamp = performance.now();

    if (stack) {
      this.isScrollStart = false;

      const mX = this.x - (stack[stack.length - 1]?.x || this.x);
      const mY = this.y - (stack[stack.length - 1]?.y || this.y);

      this.movementX = mX;
      this.movementY = mY;

      this.rateX =
        stack.reduce((sum, o) => sum + Math.abs(o.movementX), Math.abs(mX)) /
        (stack.length + 1);

      this.rateY =
        stack.reduce((sum, o) => sum + Math.abs(o.movementY), Math.abs(mY)) /
        (stack.length + 1);

      this.signX = Math.sign(mX);
      this.signY = Math.sign(mY);

      if (!this.signX || !this.signY) {
        const _stack = stack.slice().reverse();
        this.signX =
          this.signX ||
          Math.sign(_stack.find((o) => !!o.movementX)?.movementX || 0);
        this.signY =
          this.signY ||
          Math.sign(_stack.find((o) => !!o.movementY)?.movementY) ||
          0;
      }

      this.isScrolling = Math.max(this.rateX, this.rateY) >= threshold;

      this.isScrollEnd =
        !this.isScrolling && this.timeStamp - stack.timeStamp > frame;
    }
  }

  /**
   * @type {boolean}
   */
  isScrollStart;

  /**
   * @type {boolean}
   */
  isScrollEnd;

  /**
   * @type {boolean}
   */
  isScrolling;

  /**
   * @type {number}
   */
  movementX;

  /**
   * @type {number}
   */
  movementY;

  /**
   * @type {number}
   */
  rateX;

  /**
   * @type {number}
   */
  rateY;

  /**
   * @type {number}
   */
  signX;

  /**
   * @type {number}
   */
  signY;

  /**
   * @type {Element}
   */
  target;

  /**
   * @type {DOMHighResTimeStamp}
   */
  timeStamp;

  /**
   * @type {number}
   */
  x;

  /**
   * @type {number}
   */
  y;
}
